import { useMemo } from 'react';
import { useAuthStore } from '.';

type UseIsProtectedOptions = {
  roles?: string[];
  permissions?: string[];
  opt?: 'all' | 'any';
};

/**
 * The roles the user holds in the given organization (the active one, from `me`). `me` lists the active
 * organization's roles as `roles`; `memberships`, when an account record carries it, is more specific.
 */
export const getOrganizationRoles = (
  user: { roles?: string[]; memberships?: { organizationId: string; roles?: string[] }[] } | null,
  organizationId: string | null
): string[] => {
  const membership = organizationId
    ? user?.memberships?.find((m) => m.organizationId === organizationId)
    : undefined;
  return membership?.roles ?? user?.roles ?? [];
};

const checkAllRoles = (userRoles: string[] | undefined, requiredRoles: string[]): boolean => {
  if (requiredRoles.length === 0) return true;
  return requiredRoles.every((role) => userRoles?.includes(role));
};

const checkAllPermissions = (
  userPermissions: string[] | undefined,
  requiredPermissions: string[]
): boolean => {
  if (requiredPermissions.length === 0) return true;
  return requiredPermissions.every((permission) => userPermissions?.includes(permission));
};

const checkAnyRole = (userRoles: string[] | undefined, requiredRoles: string[]): boolean => {
  if (requiredRoles.length === 0) return false;
  return requiredRoles.some((role) => userRoles?.includes(role));
};

const checkAnyPermission = (
  userPermissions: string[] | undefined,
  requiredPermissions: string[]
): boolean => {
  if (requiredPermissions.length === 0) return false;
  return requiredPermissions.some((permission) => userPermissions?.includes(permission));
};

type AccessCheck = UseIsProtectedOptions & {
  user: any;
  organizationId: string | null;
};

/**
 * Pure access check shared by the route guard, in-page fragments and the sidebar filter.
 * Returns false when nothing is required — callers that treat "no requirement" as open
 * (e.g. the sidebar) must check for that themselves.
 */
export const hasAccess = ({
  user,
  organizationId,
  roles = [],
  permissions = [],
  opt = 'any',
}: AccessCheck): boolean => {
  if (!user) return false;
  if (roles.length === 0 && permissions.length === 0) return false;

  const userRoles = getOrganizationRoles(user, organizationId);

  if (opt === 'all') {
    return checkAllRoles(userRoles, roles) && checkAllPermissions(user.permissions, permissions);
  }

  return checkAnyRole(userRoles, roles) || checkAnyPermission(user.permissions, permissions);
};

export const useIsProtected = ({
  roles = [],
  permissions = [],
  opt = 'any',
}: UseIsProtectedOptions = {}) => {
  const { user, isAuthenticated, selectedOrgId } = useAuthStore();

  const isProtected = useMemo(() => {
    if (!isAuthenticated) return false;
    return hasAccess({ user, organizationId: selectedOrgId, roles, permissions, opt });
  }, [isAuthenticated, user, selectedOrgId, roles, permissions, opt]);

  return {
    isProtected,
    isAuthenticated,
    user,
  };
};
